import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  TextField,
  Button,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { withTranslation } from "react-i18next";

import { utilityTunnelConsumerType } from "common/enums";
import { useGetAccessCardApplicationStatusQuery } from "apis/UtilityTunnel/ApplicationRequest";
import InputFieldText from "Components/Common/InputFieldText";

const initialFilters = {
  selectedStatusId: "", //status
  selectedStartDate: null, //from date
  selectedEndDate: null, // to date
  accessCardHolderType: "", //card holder type
  accessCardNumber: "",
};

const FilterApplicationRequests = ({ setQuery, resetPage = () => {}, t }) => {
  const { data: { data: status = [] } = {} } =
    useGetAccessCardApplicationStatusQuery();
  const isExtraLargeScreen = useMediaQuery("(max-width: 1620px)");

  const [
    {
      selectedStatusId,
      selectedStartDate,
      selectedEndDate,
      accessCardHolderType,
      accessCardNumber,
    },
    setSelectedFilteredOption,
  ] = useState(initialFilters);

  const [statusOptions, setStatusOptions] = useState([]);
  const [applicationTypeOptions, setApplicationTypeOptions] = useState([
    { label: "All", value: "" },
    ...utilityTunnelConsumerType,
  ]);

  const [invalidDateMessage, setInvalidDateMessage] = useState({
    startDateErrorMessage: "",
    endDateErrorMessage: "",
  });

  const { roles } = useSelector((state) => state.Login);

  const changeSelectOptions = (option) => {
    return option.map((option) => ({
      label: option.name,
      value: option.id,
    }));
  };

  useEffect(() => {
    const options = changeSelectOptions(status);
    setStatusOptions([{ label: "All", value: "" }, ...options]);
  }, [status]);

  const clearBtnHandler = () => {
    resetPage();
    setSelectedFilteredOption(initialFilters);
  };

  const searchBtnHandler = (e) => {
    e.preventDefault();
    resetPage();
    setQuery((query) => {
      return {
        ...query,
        searchStatus: selectedStatusId,
        fromDate: selectedStartDate?.isValid()
          ? selectedStartDate.format("MM-DD-YYYY")
          : "",
        toDate: selectedEndDate?.isValid()
          ? selectedEndDate.format("MM-DD-YYYY")
          : "",
        accessCardHolderType,
        accessCardNumber,
      };
    });
  };

  const errorMessage = (error) => {
    switch (error) {
      case "maxDate":
        return t("Please select date that is before end date");
      case "minDate":
        return t("Please select date that is after start date");
      case "disableFuture":
        return t("You can't select future date");
      case "invalidDate":
        return t("Please enter a valid date");
      case "min1900":
        return t("Select a date that is subsequent than 01/01/1900");
      default:
        return "";
    }
  };

  return (
    <>
      <form onSubmit={searchBtnHandler}>
        <Grid container columnSpacing={2} className="mt-2">
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <Autocomplete
              className="w-100 mb-2"
              size="small"
              value={
                applicationTypeOptions?.find(
                  (application) => application.value === accessCardHolderType
                ) || ""
              }
              disableClearable
              options={applicationTypeOptions}
              renderInput={(params) => (
                <TextField {...params} label={t("Access Holder Type")} />
              )}
              onChange={(_, option) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  accessCardHolderType: option.value,
                }))
              }
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                );
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <Autocomplete
              className="w-100 mb-2"
              size="small"
              options={statusOptions}
              value={
                statusOptions?.find(
                  (status) => status.value === selectedStatusId
                ) || ""
              }
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label={t("Select Status")} />
              )}
              onChange={(_, option) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedStatusId: option.value,
                }))
              }
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                );
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <DatePicker
              label={t("Start Date")}
              className="w-100 mb-2"
              format="DD-MM-YYYY"
              value={selectedStartDate ?? null}
              disableFuture
              maxDate={selectedEndDate}
              onError={(error) =>
                setInvalidDateMessage((prevState) => ({
                  ...prevState,
                  startDateErrorMessage:
                    error === "minDate" ? "min1900" : error,
                }))
              }
              slotProps={{
                textField: {
                  size: "small",
                  helperText: errorMessage(
                    invalidDateMessage.startDateErrorMessage
                  ),
                },
              }}
              onChange={(date) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedStartDate: date,
                }))
              }
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <DatePicker
              label={t("End Date")}
              className="w-100 mb-2"
              format="DD-MM-YYYY"
              value={selectedEndDate ?? null}
              disableFuture
              onError={(error) => {
                setInvalidDateMessage((prevState) => ({
                  ...prevState,
                  endDateErrorMessage: error,
                }));
              }}
              minDate={selectedStartDate}
              slotProps={{
                textField: {
                  size: "small",
                  helperText: errorMessage(
                    invalidDateMessage.endDateErrorMessage
                  ),
                },
              }}
              onChange={(date) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedEndDate: date,
                }))
              }
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <InputFieldText
              label={t("Access Card Number")}
              className="w-100"
              value={accessCardNumber ?? ""}
              placeholder={t("Enter Access Card Number")}
              onChange={(e) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  accessCardNumber: e.target.value,
                }))
              }
            />
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              type="submit"
              className="me-3 mb-2"
              disabled={
                !!(
                  invalidDateMessage.startDateErrorMessage ||
                  invalidDateMessage.endDateErrorMessage
                )
              }
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={clearBtnHandler}
              className="me-3 mb-2"
            >
              {t("Clear")}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

FilterApplicationRequests.propTypes = {
  setQuery: PropTypes.func,
  resetPage: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(FilterApplicationRequests);
